// Schritt 4 des Übergabe-Assistenten — Schlüssel: je Schlüsselart (vorbelegt aus
// SCHLUESSEL_STANDARD: Haustür, Wohnungstür, Keller, Briefkasten, Garage, Sonstige)
// eine Zeile mit Minus/Plus und der übergebenen Anzahl in der Mitte. Über
// „Bezeichnung der weiteren Schlüsselart" + Plus (oder Enter) lässt sich eine zusätzliche,
// frei benannte Zeile anlegen (z. B. „Garten" oder „Waschküche" bei Objekten mit Sonderschlüsseln).
//
// Optik (design_handoff_uebergabe §4.4): Kachelraster mit Symbol je Art, Anzahl > 0 = Akzent-Ring,
// Summe oben rechts (nur Anzeige, Freigabe 17.10.2026).
import React, { useState } from 'react';
import { Door, DoorOpen, Garage, Key, Mailbox, Minus, Plus, Stairs, type Icon } from '@phosphor-icons/react';
import type { LokalerEntwurf } from '../../../lib/uebergabe/lokal';
import { schluesselSumme, schluesselSymbol, type SchluesselSymbol } from '../../../lib/uebergabeAnsicht/darstellung';
import { istTouch } from './assistent/Rundgangteile';

interface SchrittSchluesselProps {
  entwurf: LokalerEntwurf;
  setEntwurf: (updater: LokalerEntwurf | ((vorher: LokalerEntwurf) => LokalerEntwurf)) => void;
}

const SCHLUESSEL_ICON: Record<SchluesselSymbol, Icon> = {
  haustuer: Door, wohnungstuer: DoorOpen, keller: Stairs, briefkasten: Mailbox, garage: Garage, sonstig: Key,
};

const SchrittSchluessel: React.FC<SchrittSchluesselProps> = ({ entwurf, setEntwurf }) => {
  const { protokoll } = entwurf;
  const [neueBezeichnung, setNeueBezeichnung] = useState('');
  const touch = istTouch();
  const knopf = touch ? 44 : 34;
  const summe = schluesselSumme(protokoll.schluessel);

  const aendereAnzahl = (index: number, delta: number) => {
    setEntwurf(e => ({
      ...e,
      protokoll: {
        ...e.protokoll,
        schluessel: e.protokoll.schluessel.map((z, i) => (i === index ? { ...z, anzahl: Math.max(0, z.anzahl + delta) } : z)),
      },
    }));
  };

  const weitereArtHinzufuegen = () => {
    const bezeichnung = neueBezeichnung.trim();
    if (!bezeichnung) return;
    setEntwurf(e => ({ ...e, protokoll: { ...e.protokoll, schluessel: [...e.protokoll.schluessel, { bezeichnung, anzahl: 0 }] } }));
    setNeueBezeichnung('');
  };

  return (
    <section className="flex flex-col" style={{ gap: 10, padding: '16px 18px', borderRadius: 24, background: 'var(--glass)', boxShadow: 'var(--glass-sh)' }}>
      <div className="flex items-baseline justify-between" style={{ gap: 10 }}>
        <span style={{ fontSize: 12, fontWeight: 700, color: 'var(--ink3)' }}>Übergebene Schlüssel</span>
        <span className="tabular-nums" style={{ fontSize: 13, fontWeight: 800, color: 'var(--acc)' }}>{summe === 1 ? '1 Schlüssel' : `${summe} Schlüssel`}</span>
      </div>

      <div className="grid" style={{ gap: 8, gridTemplateColumns: `repeat(auto-fill, minmax(min(${touch ? 320 : 280}px, 100%), 1fr))` }}>
        {protokoll.schluessel.map((zeile, index) => {
          const I = SCHLUESSEL_ICON[schluesselSymbol(zeile.bezeichnung)];
          const aktiv = zeile.anzahl > 0;
          return (
            <div key={`${zeile.bezeichnung}-${index}`} className="flex items-center"
              style={{ gap: 10, padding: '10px 10px 10px 12px', borderRadius: 18, background: 'var(--fill)',
                boxShadow: aktiv ? 'inset 0 0 0 1.5px var(--acc)' : 'inset 0 0 0 1px var(--line)' }}>
              <span aria-hidden className="inline-flex shrink-0 items-center justify-center"
                style={{ width: 36, height: 36, borderRadius: 11, background: aktiv ? 'var(--acc-fill)' : 'var(--fill2)', color: aktiv ? 'var(--acc-ink)' : 'var(--ink3)' }}>
                <I size={18} weight="fill" />
              </span>
              <span className="min-w-0 flex-1 truncate" data-tip={zeile.bezeichnung} style={{ fontSize: 14, fontWeight: 700 }}>{zeile.bezeichnung}</span>
              <div className="flex shrink-0 items-center" style={{ gap: 4 }}>
                <button type="button" onClick={() => aendereAnzahl(index, -1)} disabled={zeile.anzahl === 0}
                  aria-label={`${zeile.bezeichnung}: eine weniger`} className="inline-flex items-center justify-center"
                  style={{ width: knopf, height: knopf, borderRadius: 11, border: 'none', cursor: zeile.anzahl === 0 ? 'default' : 'pointer',
                    background: 'var(--fill2)', color: 'var(--ink)', opacity: zeile.anzahl === 0 ? 0.35 : 1 }}>
                  <Minus size={12} weight="bold" />
                </button>
                <span className="tabular-nums" aria-live="polite" style={{ minWidth: 28, textAlign: 'center', fontSize: 18, fontWeight: 800 }}>{zeile.anzahl}</span>
                <button type="button" onClick={() => aendereAnzahl(index, 1)} aria-label={`${zeile.bezeichnung}: eine mehr`}
                  className="inline-flex items-center justify-center"
                  style={{ width: knopf, height: knopf, borderRadius: 11, border: 'none', cursor: 'pointer', background: 'var(--acc-soft)', color: 'var(--acc)' }}>
                  <Plus size={12} weight="bold" />
                </button>
              </div>
            </div>
          );
        })}
      </div>

      <div className="flex" style={{ gap: 8 }}>
        <input
          value={neueBezeichnung}
          onChange={e => setNeueBezeichnung(e.target.value)}
          onKeyDown={e => { if (e.key === 'Enter') { e.preventDefault(); weitereArtHinzufuegen(); } }}
          placeholder="Bezeichnung der weiteren Schlüsselart"
          aria-label="Bezeichnung der weiteren Schlüsselart"
          className="min-w-0 flex-1"
          style={{ height: 44, padding: '0 14px', borderRadius: 14, border: 'none', outline: 'none', background: 'var(--fill2)',
            fontFamily: 'inherit', fontSize: 14, fontWeight: 600, color: 'var(--ink)' }}
        />
        <button type="button" onClick={weitereArtHinzufuegen} aria-label="Weitere Schlüsselart hinzufügen"
          data-tip="Weitere Schlüsselart hinzufügen (oder Enter)" className="inline-flex shrink-0 items-center justify-center"
          style={{ width: 44, height: 44, borderRadius: 14, border: 'none', cursor: 'pointer', background: 'var(--acc-fill)', color: 'var(--acc-ink)' }}>
          <Plus size={15} weight="bold" />
        </button>
      </div>
    </section>
  );
};

export default SchrittSchluessel;
